import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';

import {
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Breadcrumb as BreadcrumbWrapper,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@app-evolution-cms/element-ui';
import { ROUTE } from '@app-evolution-cms/router';

import type { Claim } from '../users/_api/request.type';
import { useUserState } from '../users/_states/user';

const EMPTY_CLAIMS: Claim[] = [];

const SECTION_LABELS: Record<string, string> = {
  faqs: 'FAQs',
  media: 'Media',
  modules: 'Módulos',
  notification_center_templates: 'Centro de Notificaciones',
  onboardingsplash: 'Onboarding Splash',
  rules: 'Reglas',
  target_groups: 'Target Groups',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Crear',
  edit: 'Editar',
  upload: 'Importar',
};

type BreadcrumbItemDef = {
  key: string;
  label: string;
  href?: string;
};

function useBreadcrumbs(): BreadcrumbItemDef[] {
  const { pathname } = useLocation();
  const userClaims = useUserState((s) => s.user?.claims ?? EMPTY_CLAIMS);

  const claimLabelByModuleId = useMemo(() => {
    const map = new Map<string, string>();
    for (const claim of userClaims) {
      map.set(claim.module.id, claim.module.label);
    }
    return map;
  }, [userClaims]);

  return useMemo((): BreadcrumbItemDef[] => {
    const crumbs: BreadcrumbItemDef[] = [{ key: 'root', label: 'Dashboard', href: ROUTE.root }];

    if (pathname === ROUTE.root) return crumbs;

    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return crumbs;

    const firstSegment = segments[0];
    const sectionLabel =
      SECTION_LABELS[firstSegment] ?? claimLabelByModuleId.get(firstSegment) ?? firstSegment;
    const sectionHref = `/${firstSegment}`;

    const actionSegment = segments.find((s) => ACTION_LABELS[s]);

    if (actionSegment) {
      crumbs.push({ key: `section-${firstSegment}`, label: sectionLabel, href: sectionHref });
      crumbs.push({ key: `action-${actionSegment}`, label: ACTION_LABELS[actionSegment] });
    } else {
      crumbs.push({ key: `section-${firstSegment}`, label: sectionLabel });
    }

    return crumbs;
  }, [pathname, claimLabelByModuleId]);
}

export function Breadcrumb() {
  const items = useBreadcrumbs();
  const listRef = useRef<HTMLOListElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const fullWidthRef = useRef(0);

  // Reset to expanded state on every navigation (items reference changes)
  useLayoutEffect(() => {
    setCollapsed(false);
    fullWidthRef.current = 0;
  }, [items]);

  // Measure content width and collapse middle items if they overflow
  useLayoutEffect(() => {
    if (collapsed) return;

    const el = listRef.current;
    if (!el) return;

    fullWidthRef.current = el.scrollWidth;

    if (el.scrollWidth > el.clientWidth) {
      setCollapsed(true);
    }
  }, [items, collapsed]);

  // Track container width changes via ResizeObserver
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      if (fullWidthRef.current === 0) return;
      setCollapsed(fullWidthRef.current > entry.contentRect.width);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const first = items[0];
  const last = items.length > 1 ? items[items.length - 1] : null;
  const middle = items.slice(1, items.length > 1 ? items.length - 1 : items.length);

  return (
    <BreadcrumbWrapper className="min-w-0 flex-1 overflow-hidden">
      <BreadcrumbList ref={listRef} className="flex-nowrap">
        {/* Dashboard – always visible */}
        <BreadcrumbItem>
          <BreadcrumbLink asChild tabIndex={-1}>
            <Link
              className="rounded-3xl"
              id="PER-navigation-breadcrumb-dashboard"
              tabIndex={-1}
              to={ROUTE.root}
            >
              <Button className="text-sm" variant="ghost">
                {first.label}
              </Button>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {/* Middle items – collapse into dropdown when they don't fit */}
        {middle.length > 0 && last && (
          <>
            <BreadcrumbSeparator />
            <AnimatePresence mode="wait" initial={false}>
              {collapsed ? (
                <motion.li
                  key="collapsed"
                  className="inline-flex items-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger aria-label="Ver secciones anteriores" asChild>
                      <Button className="text-sm" size="icon" variant="ghost">
                        <BreadcrumbEllipsis />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {middle.map((item) => (
                        <DropdownMenuItem key={item.key} asChild>
                          {item.href ? (
                            <Link className="rounded-3xl" to={item.href}>
                              {item.label}
                            </Link>
                          ) : (
                            <span>{item.label}</span>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </motion.li>
              ) : (
                <motion.li
                  key="expanded"
                  className="inline-flex items-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  {middle.map((item, i) => (
                    <span key={item.key} className="inline-flex items-center">
                      {i > 0 && <BreadcrumbSeparator />}
                      {item.href ? (
                        <BreadcrumbLink asChild tabIndex={-1}>
                          <Link tabIndex={-1} className="rounded-3xl" to={item.href}>
                            <Button className="text-sm" variant="ghost">
                              {item.label}
                            </Button>
                          </Link>
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage tabIndex={-1}>
                          <Button className="text-sm" disabled variant="link">
                            {item.label}
                          </Button>
                        </BreadcrumbPage>
                      )}
                    </span>
                  ))}
                </motion.li>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Current page – always visible, never a link */}
        {last && (
          <>
            <BreadcrumbSeparator />
            <AnimatePresence mode="wait" initial={false}>
              <motion.li
                key={last.key}
                className="inline-flex items-center"
                initial={{ opacity: 0, x: 6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                <BreadcrumbPage tabIndex={-1}>
                  <Button className="text-sm" disabled variant="link">
                    {last.label}
                  </Button>
                </BreadcrumbPage>
              </motion.li>
            </AnimatePresence>
          </>
        )}
      </BreadcrumbList>
    </BreadcrumbWrapper>
  );
}
